import React from 'react';
import { Box, LinearProgress, Typography, Tooltip } from '@mui/material';

// Visual % bar for a v2 WO or tree. Handles 0%, partial, 100%, and the
// planned=0 divide-by-zero edge case.
export default function ProgressIndicator({
  planned = 0,
  produced = 0,
  scrapped = 0,
  label,
  showNumbers = true,
  height = 8,
}) {
  const safePlanned = Number(planned) || 0;
  const safeProduced = Number(produced) || 0;
  const safeScrapped = Number(scrapped) || 0;

  let percent = 0;
  let unitsLabel;
  if (safePlanned <= 0) {
    // Divide-by-zero: clearly communicate "no plan set" while staying ≥0%.
    percent = 0;
    unitsLabel = 'planned 0';
  } else {
    percent = Math.min(100, Math.max(0, (safeProduced / safePlanned) * 100));
    unitsLabel = `${safeProduced} / ${safePlanned}`;
  }

  const isComplete = safePlanned > 0 && safeProduced >= safePlanned;

  return (
    <Box data-testid="progress-indicator" sx={{ width: '100%' }}>
      {(label || showNumbers) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
          {label && (
            <Typography variant="caption" color="text.secondary">{label}</Typography>
          )}
          {showNumbers && (
            <Typography
              variant="caption"
              color={isComplete ? 'success.main' : 'text.secondary'}
              data-testid="progress-numbers"
            >
              {unitsLabel} ({percent.toFixed(0)}%)
              {safeScrapped > 0 ? ` · scrap ${safeScrapped}` : ''}
            </Typography>
          )}
        </Box>
      )}
      <Tooltip title={`${percent.toFixed(1)}% complete`} arrow>
        <LinearProgress
          variant="determinate"
          value={percent}
          color={isComplete ? 'success' : 'primary'}
          sx={{ height, borderRadius: height / 2 }}
          aria-label={label || 'progress'}
          aria-valuenow={Math.round(percent)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </Tooltip>
    </Box>
  );
}
